import Dialog from '@mui/material/Dialog'
import Composer from 'components/Composer'
import { ComposerMediaContext } from 'components/ComposerMediaContext'
import { parse } from 'query-string'
import { useCallback, useContext, useEffect, useRef } from 'react'
import { useDispatch, useSelector, useStore } from 'react-redux'
import { useLocation, useNavigate } from 'react-router'
import {
  addComposerImages,
  addImagePosts,
  getLinkDetails,
  removeComposerImages,
  resetComposer,
  setComposerBulkUploadFileUrl,
  setComposerStatus
} from 'services/compose'
import { composerImagesSelector, composerIsEmptySelector } from 'services/compose/selectors'
import { COMPOSER_STATUS_KEY_GENERIC } from 'shared/types'
import StoreState, { StoreThunkDispatch } from 'store/state'
import styles from './Compose.pcss'

export function Compose() {
  const dispatch = useDispatch<StoreThunkDispatch>()
  const store = useStore<StoreState>()
  const { dispatch: contextDispatch } = useContext(ComposerMediaContext)
  const navigate = useNavigate()
  const location = useLocation()
  const isEmpty = useSelector(composerIsEmptySelector)
  const prevLocationState = useRef<any>({})
  const handledSearch = useRef<string | undefined>(undefined)

  const query = parse(location.search)
  const { shareUrl, shareImageUrl, shareImageUrls, imageSource, multi } = query

  useEffect(() => {
    // NOTE: every branch below clears `composer.images`, so reading them through `useSelector` and
    // listing them as a dependency re-ran this effect (and re-added the posts) on each dispatch.
    // `navigate` only drops the query params in a low priority transition, so it lost that race and
    // the import repeated until React bailed out. Import once per query instead, and read the
    // images at dispatch time.
    if (handledSearch.current === location.search) {
      return
    }
    handledSearch.current = location.search
    const images = composerImagesSelector(store.getState())

    if (typeof shareUrl === 'string') {
      const url = decodeURIComponent(shareUrl)
      dispatch(resetComposer())
      dispatch(getLinkDetails({ url }))
      dispatch(setComposerStatus({ text: url, html: url, network: COMPOSER_STATUS_KEY_GENERIC }))
      navigate('/composer', { replace: true })
    } else if (typeof shareImageUrl === 'string') {
      const imgUrl = decodeURIComponent(shareImageUrl)
      dispatch(removeComposerImages({ ids: Object.keys(images), contextDispatch }))
      dispatch(addComposerImages({ items: [{ url : imgUrl }], contextDispatch }))
      if (typeof imageSource === 'string') {
        const status = `Image source: ${imageSource}`
        dispatch(setComposerStatus({ text: status, html: status, network: COMPOSER_STATUS_KEY_GENERIC }))
      }
      navigate('/composer', { replace: true })
    } else if (typeof shareImageUrls === 'string') {
      // NOTE: backward compatibility for PP extension v1 where separator was ','
      const separator = shareImageUrls.includes('|') ? '|' : ','
      const urls = decodeURIComponent(shareImageUrls).split(separator).filter(Boolean)
      if (multi) {
        dispatch(addImagePosts(urls.map(url => ({ url }))))
      } else {
        dispatch(removeComposerImages({ ids: Object.keys(images), contextDispatch }))
        dispatch(addComposerImages({ items: urls.map(url => ({ url  })), contextDispatch }))
      }
      navigate('/composer', { replace: true })
    }
  }, [contextDispatch, dispatch, imageSource, location.search, multi, navigate, shareImageUrl, shareImageUrls, shareUrl, store])

  useEffect(() => {
    if (location.state?.opener !== '/composer') {
      prevLocationState.current = location.state
    }
  }, [location.state])

  const onUnload = useCallback((event: Event) => {
    event.preventDefault()
    event.returnValue = false
    return ''
  }, [])

  useEffect(() => {
    if (__DEV__) {
      return
    }
    if (!isEmpty) {
      window.addEventListener('beforeunload', onUnload)
    }
    return () => {
      window.removeEventListener('beforeunload', onUnload)
    }
  }, [isEmpty, onUnload])

  const close = (reset?: boolean, redirectUrl?: string) => {
    if (redirectUrl) {
      navigate(redirectUrl)
    } else if (prevLocationState.current) {
      const { opener, search, nextState } = prevLocationState.current
      navigate(opener || '/', { state: { search, ...nextState } })
    } else {
      navigate('/')
    }

    dispatch(setComposerBulkUploadFileUrl({ url: undefined }))
    if (reset) {
      dispatch(resetComposer())
    }
  }

  return (
    <Dialog
      open
      fullScreen
      disableEnforceFocus
      classes={{ root: styles.dialog, paper: styles.paper }}
      data-testid="composer-dialog"
    >
      <Composer onClose={close} />
    </Dialog>
  )
}

export default Compose
